import { useState, useEffect, useRef } from 'react';

export default function About() {
  const [yearsCount, setYearsCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const badgeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animateCounter();
        }
      },
      { threshold: 0.5 }
    );

    if (badgeRef.current) {
      observer.observe(badgeRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateCounter = () => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    const target = 15;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      setYearsCount(Math.floor(target * progress));

      if (currentStep >= steps) {
        setYearsCount(target);
        clearInterval(timer);
      }
    }, interval);
  };

  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Your Trusted Partner in Business Excellence
            </h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Auralis Consulting is a premier business advisory firm dedicated to empowering organizations with strategic insights, innovative solutions, and expert guidance. With over 15 years of industry experience, we specialize in corporate strategy development, tax optimization, international business expansion, and comprehensive IT network management.
            </p>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Our team of seasoned consultants brings together diverse expertise across finance, technology, international business, and strategic management. We work closely with clients to understand their unique challenges, identify opportunities for growth, and implement tailored solutions that deliver measurable results and sustainable competitive advantages.
            </p>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              At Auralis Consulting, we believe in building long-term partnerships based on trust, transparency, and exceptional service delivery. Our client-centric approach ensures that every engagement is customized to meet specific business objectives while maintaining the highest standards of professional excellence and ethical business practices.
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-blue-50 rounded-xl p-6">
                <i className="ri-award-line text-4xl text-blue-600 mb-3"></i>
                <h4 className="font-bold text-gray-900 mb-2">Industry Recognition</h4>
                <p className="text-gray-600 text-sm">Award-winning consulting excellence</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-6">
                <i className="ri-shield-check-line text-4xl text-blue-600 mb-3"></i>
                <h4 className="font-bold text-gray-900 mb-2">Certified Experts</h4>
                <p className="text-gray-600 text-sm">Highly qualified professional team</p>
              </div>
            </div>
          </div>
          <div className="relative">
            <img 
              src="https://public.readdy.ai/ai/img_res/fd15cf93ee8550bed259bb9bcc9a0554.jpg"
              alt="Auralis Consulting Team"
              className="rounded-2xl shadow-2xl w-full h-full object-cover object-top"
            />
            <div ref={badgeRef} className="absolute -bottom-8 -left-8 bg-blue-600 text-white p-8 rounded-xl shadow-xl">
              <div className="text-5xl font-bold mb-2">{yearsCount}+</div>
              <div className="text-lg font-medium">Years of Excellence</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
